import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    output,
    effect,
    computed,
    Renderer2,
    signal,
    ViewEncapsulation,
    OnInit,
    DestroyRef,
} from '@angular/core';
import { FiMediaBankService } from '@fi-app/shared/media-bank/media-bank.service';
import { FileType } from '@fi-app/shared/types/file-type';
import { IMediaBankFile } from '@fi-shared/media-bank/media-bank';
import { isDefined } from '@fi-app/shared/utils';
import { Subject } from 'rxjs';
import { IVideoProgressEvent } from '@fi-shared/media-bank/fi-vimeo/fi-vimeo-player-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FiOnboardingStepCompletionDialogState } from '../../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';

@Component({
    selector: 'fi-media-bank-completion',
    templateUrl: './mediabank-completion.component.html',
    styleUrl: './mediabank-completion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'fi-media-bank-completion',
        '[class.downloadable]': 'isDownloadableMediaBankFile()',
        '[class.step-completed]': 'isStepCompleted()',
    },
    standalone: false,
})
export class FiMediaBankCompletionComponent implements OnInit {
    public isInProgress = input<boolean>(false);
    public isStepCompleted = input<boolean>(false);
    public failedToLoadElement = output<void>();
    public setInProgress = output<void>();

    private readonly fiMediaBankService = inject(FiMediaBankService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly hostElementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);
    private readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    protected mediaBankFile = signal<IMediaBankFile | undefined>(undefined);
    protected loading = signal<boolean>(false);
    private isTimerFinished = signal<boolean>(false);

    protected readonly timerValueMS: number = 15000;
    private readonly videoProgressThreshold: number = 90;

    protected isTimerHidden = computed(() => {
        return (
            !isDefined(this.mediaBankFile()) ||
            this.isDownloadableMediaBankFile() ||
            this.isVideoAttachment() ||
            this.isStepCompleted() ||
            this.isTimerFinished() ||
            this.isInProgress() ||
            this.completionDialogState.isReportMode()
        );
    });

    public isDownloadableMediaBankFile = computed<boolean>(() => {
        return (
            this.mediaBankFile()?.FileType !== FileType.Movie &&
            this.mediaBankFile()?.FileType !== FileType.Image &&
            this.mediaBankFile()?.FileType !== FileType.Pdf
        );
    });

    private isVideoAttachment = computed<boolean>(() => {
        return this.mediaBankFile()?.FileType === FileType.Movie;
    });

    private _setIframeDialogPanelClassEffect = effect((onCleanup) => {
        const mediaBankFile = this.mediaBankFile();
        if (!mediaBankFile) {
            return;
        }

        const overlayPane = this.hostElementRef.nativeElement.closest('.cdk-overlay-pane') as HTMLElement | null;
        if (!overlayPane) {
            return;
        }

        const isIframePreview = mediaBankFile.FileType === FileType.Pdf;

        if (isIframePreview) {
            this.renderer.addClass(overlayPane, 'fi-onboarding-step-completion-dialog-panel-mediabank-iframe');
            this.renderer.addClass(this.hostElementRef.nativeElement, 'iframe-mode');
        } else {
            this.renderer.removeClass(overlayPane, 'fi-onboarding-step-completion-dialog-panel-mediabank-iframe');
            this.renderer.removeClass(this.hostElementRef.nativeElement, 'iframe-mode');
        }

        onCleanup(() => {
            this.renderer.removeClass(overlayPane, 'fi-onboarding-step-completion-dialog-panel-mediabank-iframe');
            this.renderer.removeClass(this.hostElementRef.nativeElement, 'iframe-mode');
        });
    });

    protected readonly videoProgressListener$ = new Subject<IVideoProgressEvent>();

    public ngOnInit(): void {
        this.subscribeOnVideoProgress();
        this.loadMediaBankFile();
    }

    private subscribeOnVideoProgress(): void {
        const videoProgressSubscription = this.videoProgressListener$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: IVideoProgressEvent) => {
                if (event.percent >= this.videoProgressThreshold) {
                    this.onMediaBankFileViewed();
                    videoProgressSubscription.unsubscribe();
                }
            });
    }

    private loadMediaBankFile(): void {
        const id = this.completionDialogState.stepCompletionConfig.onboardingStep.ObjectId;
        this.loading.set(true);
        this.fiMediaBankService
            .getMediaBankFile(id, false)
            .then((mediaBankFile: IMediaBankFile) => {
                this.mediaBankFile.set(mediaBankFile);
            })
            .catch((error) => {
                this.failedToLoadElement.emit();
            })
            .finally(() => {
                this.loading.set(false);
            });
    }

    protected onMediaBankFileViewed() {
        this.setInProgress.emit();
        this.completionDialogState.setSaveButtonDisabled(false);
        this.isTimerFinished.set(true);
    }
}
